import React, { memo, useMemo, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';

import 'chartjs-adapter-date-fns';
import type { ChartOptions, ChartData } from 'chart.js';
import { useBatteryChart } from './hooks/useBatteryChart';
import { BatteryHistoryItem } from '../../types/api';

// Custom vertical line plugin
const verticalLinePlugin = {
  id: 'verticalLine',
  beforeTooltipDraw: (chart: any, args: any, options: any) => {
    if (options.timestamp) {
      const ctx = chart.ctx;
      const x = chart.scales.x.getPixelForValue(new Date(options.timestamp).getTime());
      const topY = chart.scales.battery.top;
      const bottomY = chart.scales.battery.bottom;
      
      ctx.save();
      ctx.strokeStyle = '#ff6b35';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.stroke();
      ctx.restore();
    }
  }
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin,
  verticalLinePlugin
);

interface BatteryChartOptimizedProps {
  data: BatteryHistoryItem[];
  allData: BatteryHistoryItem[];
  height?: number;
  highlightedTimestamp?: string | null;
  onHighlightChange?: (timestamp: string | null) => void;
  onRangeChange?: (data: BatteryHistoryItem[]) => void;
  autoFit?: boolean;
}

const BatteryChartOptimized: React.FC<BatteryChartOptimizedProps> = memo(({ 
  data, 
  allData,
  height = 300, 
  highlightedTimestamp = null,
  onHighlightChange,
  onRangeChange,
  autoFit = false
}) => {
  const { processedChartData, chartRef } = useBatteryChart({ data: allData }); // Always use all data for chart

  // Memoized chart data to prevent unnecessary re-renders
  const chartData: ChartData<'line'> = useMemo(() => ({
    datasets: [
      {
        label: '充電中',
        data: processedChartData.charging,
        borderColor: '#52c41a',
        backgroundColor: 'rgba(82, 196, 26, 0.1)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        yAxisID: 'battery',
        spanGaps: 5 * 60 * 1000,
      },
      {
        label: '放電中',
        data: processedChartData.discharging,
        borderColor: '#ff4d4f',
        backgroundColor: 'rgba(255, 77, 79, 0.1)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        yAxisID: 'battery',
        spanGaps: 5 * 60 * 1000,
      },
      {
        label: '予測満充電時間',
        data: processedChartData.remainingCharging,
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24, 144, 255, 0.1)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        yAxisID: 'remaining',
        spanGaps: 5 * 60 * 1000,
      },
      {
        label: '予測完全放電時間',
        data: processedChartData.remainingDischarging,
        borderColor: '#722ed1',
        backgroundColor: 'rgba(114, 46, 209, 0.1)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        yAxisID: 'remaining',
        spanGaps: 5 * 60 * 1000,
      },
      {
        label: '温度',
        data: processedChartData.temperature,
        borderColor: '#faad14',
        backgroundColor: 'rgba(250, 173, 20, 0.1)',
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 4,
        yAxisID: 'temperature',
        spanGaps: 5 * 60 * 1000,
      },
    ],
  }), [processedChartData]);

  // Auto-fit chart to data range only when autoFit is true
  React.useEffect(() => {
    if (autoFit && chartRef.current && data.length > 0) {
      const chart = chartRef.current;
      const times = data.map(item => new Date(item.measured_at.replace(/Z$/, '')).getTime());
      const min = Math.min(...times);
      const max = Math.max(...times);
      chart.zoomScale('x', { min, max }, 'none');
    }
  }, [data, autoFit]);



  // Memoized chart options
  const options: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: {
      mode: 'nearest',
      intersect: false,
      axis: 'x',
    },
    onHover: (event, elements) => {
      if (elements.length > 0) {
        // Find the element from battery datasets (charging or discharging) for consistent timestamp
        const batteryElement = elements.find(el => el.datasetIndex === 0 || el.datasetIndex === 1);
        const element = batteryElement || elements[0];
        
        const datasetIndex = element.datasetIndex;
        const dataIndex = element.index;
        const dataset = chartData.datasets[datasetIndex];
        if (dataset && dataset.data[dataIndex]) {
          const point = dataset.data[dataIndex] as { x: number; y: number };
          const timestamp = new Date(point.x).toISOString();
          onHighlightChange?.(timestamp);
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            return new Date(context[0].parsed.x).toLocaleString('ja-JP', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            });
          },
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            if (label === '温度') {
              return `${label}: ${value}℃`;
            }
            if (label === '予測満充電時間' || label === '予測完全放電時間') {
              const hours = Math.floor(value / 60);
              const minutes = value % 60;
              return `${label}: ${hours}時間${minutes}分`;
            }
            return `${label}: ${value}%`;
          },
        },
      },
      zoom: {
        limits: {
          x: { max: Date.now() },
        },
        zoom: {
          wheel: {
            enabled: true,
            speed: 0.1,
          },
          pinch: {
            enabled: true,
          },
          mode: 'x',
          onZoomComplete: (chart: any) => {
            if (!chart?.scales?.x || !onRangeChange) return;
            const { min, max } = chart.scales.x;
            const rangeData = allData.filter(item => {
              const time = new Date(item.measured_at).getTime();
              return time >= min && time <= max;
            });
            onRangeChange(rangeData);
          },
        },
        pan: {
          enabled: true,
          mode: 'x',
          onPanComplete: (chart: any) => {
            if (!chart?.scales?.x || !onRangeChange) return;
            const { min, max } = chart.scales.x;
            const rangeData = allData.filter(item => {
              const time = new Date(item.measured_at).getTime();
              return time >= min && time <= max;
            });
            onRangeChange(rangeData);
          },
        },
      },
      verticalLine: {
        timestamp: null
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          displayFormats: {
            hour: 'MM/dd HH:mm',
            day: 'MM/dd',
            minute: 'HH:mm',
          },
        },
        title: {
          display: true,
          text: '時刻',
        },
      },
      battery: {
        type: 'linear',
        display: true,
        position: 'left',
        min: 0,
        max: 100,
        title: {
          display: true,
          text: 'バッテリー (%)',
        },
      },
      temperature: {
        type: 'linear',
        display: true,
        position: 'right',
        offset: true,
        title: {
          display: true,
          text: '温度 (℃)',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
      remaining: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: '予測残時間 (分)',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  }), [allData, onRangeChange]);

  // Update vertical line without triggering chart update
  React.useEffect(() => {
    if (chartRef.current && chartRef.current.options) {
      const chart = chartRef.current;
      if (!chart.options.plugins) chart.options.plugins = {};
      if (!chart.options.plugins.verticalLine) chart.options.plugins.verticalLine = {};
      chart.options.plugins.verticalLine.timestamp = highlightedTimestamp;
      
      // Show tooltip at vertical line position if timestamp exists
      if (highlightedTimestamp) {
        const targetTime = new Date(highlightedTimestamp).getTime();
        const activeElements = [];
        
        // Check all datasets for matching timestamp
        const chargingIndex = processedChartData.charging.findIndex(point => point.x === targetTime);
        if (chargingIndex !== -1) {
          activeElements.push({ datasetIndex: 0, index: chargingIndex });
        }
        
        const dischargingIndex = processedChartData.discharging.findIndex(point => point.x === targetTime);
        if (dischargingIndex !== -1) {
          activeElements.push({ datasetIndex: 1, index: dischargingIndex });
        }
        
        const remainingChargingIndex = processedChartData.remainingCharging.findIndex(point => point.x === targetTime);
        if (remainingChargingIndex !== -1) {
          activeElements.push({ datasetIndex: 2, index: remainingChargingIndex });
        }
        
        const remainingDischargingIndex = processedChartData.remainingDischarging.findIndex(point => point.x === targetTime);
        if (remainingDischargingIndex !== -1) {
          activeElements.push({ datasetIndex: 3, index: remainingDischargingIndex });
        }
        
        const temperatureIndex = processedChartData.temperature.findIndex(point => point.x === targetTime);
        if (temperatureIndex !== -1) {
          activeElements.push({ datasetIndex: 4, index: temperatureIndex });
        }
        
        if (activeElements.length > 0) {
          const x = chart.scales.x.getPixelForValue(targetTime);
          chart.tooltip.setActiveElements(activeElements, { x, y: 0 });
        }
      } else {
        chart.tooltip.setActiveElements([]);
      }
      
      chart.update('none');
    }
  }, [highlightedTimestamp, processedChartData]);

  return (
    <div style={{ height }}>
      <Line ref={chartRef} data={chartData} options={options} />
    </div>
  );
});

BatteryChartOptimized.displayName = 'BatteryChartOptimized';

export default BatteryChartOptimized;